/**
 * The wizard's Magic Email endpoints: the reply library (list, one reply, draft, publish, switch,
 * preview) and the conversation log (emails, one thread, the per-thread assistant switch).
 * Server side: packages/server/src/magicEmail/library/editor.ts and magicEmail/wizard/.
 *
 * A reply's words travel as blocks (`DocumentBlock`), never as the server's template text.
 */
import { WaterlilyClient } from '@/api/waterlily';

export type VersionStatus = 'draft' | 'published' | 'superseded';
export type ActionMode =
  | 'READ_ONLY'
  | 'HUMAN_ROUTE'
  | 'SECURE_STEP'
  | 'PROPOSAL_ONLY'
  | 'CONFIRMED_WRITE';

export type InlineItem =
  | { type: 'text'; text: string }
  | { type: 'field'; name: string }
  | { type: 'break' };
export interface ParagraphBlock {
  type: 'paragraph';
  content: InlineItem[];
}
export type DocumentBlock =
  | ParagraphBlock
  | { type: 'condition'; flag: string; then: ParagraphBlock[]; otherwise: ParagraphBlock[] }
  | { type: 'legal'; text: string };

export interface TemplateAction {
  id: string;
  code: string;
  label: string;
  contract: string;
  mode: ActionMode;
}

/** A reply as the editor holds it. */
export interface EditableReply {
  title: string;
  category: string;
  route: string;
  exampleEmails: string[];
  document: DocumentBlock[] | null;
  /** Set when the stored words could not be turned into blocks. */
  documentError: string | null;
  eligibility: string[];
  actions: TemplateAction[];
  humanOwner: string;
  escalationPriority: 'P0' | 'P1' | 'P2';
}

/** `edit`: staff can put it right here. `waterlily`: it needs something Waterlily can't do yet. */
export interface ReplyProblem {
  fix: 'edit' | 'waterlily';
  message: string;
}

export type ReplyStatus = 'live' | 'draft' | 'unavailable';

export interface ReplyListEntry {
  entryId: string;
  title: string;
  category: string;
  summary: string;
  status: ReplyStatus;
  publishedVersion: number | null;
  hasUnpublishedChanges: boolean;
  problems: ReplyProblem[];
  updatedDateTime: string;
  /** How many example emails describe it to the assistant. */
  exampleCount: number;
}

export interface CatalogField {
  name: string;
  label: string;
  /** `code` fills it in; `write` must be replaced with staff's own words; `none` Waterlily can't fill yet. */
  kind: 'code' | 'write' | 'none';
  group: 'client' | 'general' | null;
  offered: boolean;
  meaning: string | null;
}

export interface ReplyCatalog {
  fields: CatalogField[];
  conditions: { name: string; label: string; meaning: string }[];
  steps: { code: string; label: string; contract: string; mode: ActionMode }[];
  owners: { value: string; label: string }[];
}

export interface ReplyVersion {
  version: number;
  status: VersionStatus;
  createdBy: string;
  createdDateTime: string;
  publishedBy: string | null;
  publishedDateTime: string | null;
  content: EditableReply | null;
  problems: ReplyProblem[];
}

export interface ReplyDetail {
  entry: { entryId: string; enabled: boolean; publishedVersion: number | null };
  /** Newest first. */
  versions: ReplyVersion[];
}

export interface ReplyPreview {
  html: string | null;
  subject: string | null;
  from: string | null;
  conditions: { name: string; label: string; value: boolean }[];
  problems: ReplyProblem[];
}

export interface ConversationEmail {
  inboundMessageId: string;
  threadKey: string;
  advisorId: string;
  advisorEmail: string | null;
  receivedDateTime: string;
  excerpt: string;
  path: 'library' | 'agent';
  outcome: 'sent' | 'handed_off' | null;
  reason: string | null;
  templateId: string | null;
  drafts: string[];
}

export interface ConversationPage {
  emails: ConversationEmail[];
  nextBefore: string | null;
}

export interface ConversationFilters {
  email?: string;
  from?: string;
}

export interface ThreadSelection {
  outcome: 'sent' | 'handed_off';
  reason: string | null;
  templateId: string | null;
  templateVersion: number | null;
  contentHash: string | null;
  clientId: string | null;
  selection: {
    severalRequests?: number;
    category?: {
      choice: string;
      confidence: number;
      top: { value: string; probability: number }[];
      reasked: boolean;
    };
    template?: {
      choice: string;
      confidence: number;
      top: { value: string; probability: number }[];
      reasked: boolean;
    };
    client?: { status: string; via?: string; reason?: string; matches?: number };
  } | null;
  actions: { id: string; code: string; label: string }[] | null;
  durationMs: number | null;
}

export interface ThreadDraft {
  origin: 'withheld' | 'parallel';
  kind: 'reply' | 'handoff';
  scenario: string | null;
  fallbackReason: string | null;
  model: string | null;
  createdDateTime: string;
  /** Null unless the viewer has the magicEmailDraftReviewer flag. */
  text: string | null;
}

export interface ThreadExchange {
  inbound: { messageId: string | null; content: string; receivedDateTime: string } | null;
  replies: { content: string; model: string | null; sentDateTime: string; tools: string[] }[];
  selection: ThreadSelection | null;
  drafts: ThreadDraft[];
}

export interface ThreadDetail {
  threadKey: string;
  advisorId: string;
  advisorEmail: string | null;
  canReviewDrafts: boolean;
  assistant: { off: boolean; changedBy: string | null; changedDateTime: string | null };
  handoff: {
    status: 'open' | 'closed';
    reason: string;
    intercomTicketId: string | null;
    intercomUrl: string | null;
    openedDateTime: string;
    closedDateTime: string | null;
  } | null;
  exchanges: ThreadExchange[];
}

export type ReplyStatusInTest = 'live' | 'draft' | 'editing';

export interface TestOption {
  value: string;
  label: string;
  probability: number;
  status?: ReplyStatusInTest | null;
}

export interface TestStep {
  choice: string;
  confidence: number;
  /** Confident enough, and far enough ahead of the runner-up, to be taken. */
  sure: boolean;
  reasked: boolean;
  options: TestOption[];
  reaskedOptions: TestOption[] | null;
}

export interface ExampleAdded {
  entryId: string;
  version: number;
  /** False when the reply already had this example. */
  added: boolean;
  /** Whether the assistant reads it: only a reply's first six examples are. */
  read: boolean;
  exampleCount: number;
}

export interface EmailTestRequest {
  email: string;
  advisorEmail?: string;
  subject?: string;
  history?: { role: 'advisor' | 'assistant'; content: string }[];
  includeDrafts?: boolean;
  /** One reply that competes as its latest draft even without `includeDrafts`. */
  draftOf?: string | null;
  editing?: { entryId: string; content: EditableReply } | null;
  selectionOnly?: boolean;
}

export interface EmailTestResult {
  advisor: { email: string; name: string | null };
  outcome: 'reply' | 'person';
  reason: string | null;
  personCategory: string | null;
  reply: { entryId: string; title: string; status: ReplyStatusInTest | null } | null;
  client: { status: 'resolved' | 'ambiguous' | 'not_found' | 'none' | 'not_read'; name: string | null; via?: string };
  competing: number;
  selection: { severalRequests: number | null; category: TestStep | null; template: TestStep | null };
  email: { subject: string; html: string; text: string } | null;
  target: {
    entryId: string;
    categoryChance: number;
    withinCategoryChance: number | null;
    chance: number | null;
    chosen: boolean;
  } | null;
}

export interface EmailTestDraft {
  locked: boolean;
  kind: 'reply' | 'handoff' | null;
  text: string | null;
}

export interface Placeholder {
  field: string;
  label: string;
  entryIds: string[];
}

export const wizardMagicEmailKeys = {
  all: ['wizardMagicEmail'] as const,
  library: () => ['wizardMagicEmail', 'library'] as const,
  placeholders: () => ['wizardMagicEmail', 'placeholders'] as const,
  catalog: () => ['wizardMagicEmail', 'catalog'] as const,
  entry: (entryId: string) => ['wizardMagicEmail', 'entry', entryId] as const,
  conversations: (filters: ConversationFilters) =>
    ['wizardMagicEmail', 'conversations', filters] as const,
  thread: (advisorId: string, threadKey: string) =>
    ['wizardMagicEmail', 'thread', advisorId, threadKey] as const,
};

export const wizardMagicEmailApi = {
  fetchLibrary: () =>
    WaterlilyClient.get<{ entries: ReplyListEntry[] }>('/wizard/magic-email-library'),
  fetchCatalog: () => WaterlilyClient.get<ReplyCatalog>('/wizard/magic-email-library-catalog'),
  fetchEntry: (entryId: string) =>
    WaterlilyClient.get<ReplyDetail>('/wizard/magic-email-library-entry', { entryId }),
  saveDraft: (body: { entryId: string; content: EditableReply }) =>
    WaterlilyClient.post<{ entryId: string; version: number }>(
      '/wizard/magic-email-library-draft',
      body,
    ),
  publish: (body: { entryId: string; version: number }) =>
    WaterlilyClient.post<{ entryId: string; publishedVersion: number; enabled: boolean }>(
      '/wizard/magic-email-library-publish',
      body,
    ),
  setEnabled: (body: { entryId: string; enabled: boolean }) =>
    WaterlilyClient.put<{ entryId: string; enabled: boolean }>(
      '/wizard/magic-email-library-enabled',
      body,
    ),
  preview: (body: {
    content: EditableReply;
    flags?: Record<string, boolean>;
    highlight?: boolean;
  }) => WaterlilyClient.post<ReplyPreview>('/wizard/magic-email-library-preview', body),
  publishAll: () =>
    WaterlilyClient.post<{ published: number; switchedOn: number }>('/wizard/magic-email-library-publish-all', {}),
  fetchPlaceholders: () => WaterlilyClient.get<{ placeholders: Placeholder[] }>('/wizard/magic-email-library-placeholders'),
  fillPlaceholder: (body: { field: string; words: string }) =>
    WaterlilyClient.post<{ field: string; updated: string[] }>('/wizard/magic-email-library-fill', body),
  addExample: (body: { entryId: string; email: string }) =>
    WaterlilyClient.post<ExampleAdded>('/wizard/magic-email-library-example', body),
  testEmail: (body: EmailTestRequest) => WaterlilyClient.post<EmailTestResult>('/wizard/magic-email-test', body),
  testEmailDraft: (body: Omit<EmailTestRequest, 'includeDrafts' | 'editing' | 'selectionOnly'>) =>
    WaterlilyClient.post<EmailTestDraft>('/wizard/magic-email-test-draft', body),
  importSeed: () =>
    WaterlilyClient.post<{ added: number; existing: number; invalid: string[] }>(
      '/wizard/magic-email-library-import',
      {},
    ),
  fetchConversations: (filters: ConversationFilters & { before?: string }) =>
    WaterlilyClient.get<ConversationPage>('/wizard/magic-email-conversations', filters),
  fetchThread: (advisorId: string, threadKey: string) =>
    WaterlilyClient.get<ThreadDetail>('/wizard/magic-email-conversation', { advisorId, threadKey }),
  setThreadAssistant: (body: { advisorId: string; threadKey: string; assistantOn: boolean }) =>
    WaterlilyClient.put<{ off: boolean; changedBy: string; changedDateTime: string }>(
      '/wizard/magic-email-thread-assistant',
      body,
    ),
};
